import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { formatCurrency, fromBaseCurrency, toBaseCurrency, API_URL } from "../../lib/utils";
import { SalaryHistoryList } from "./SalaryHistoryList";
import { REASON_TYPES } from "./salaryReasons";

export const SalaryAdjustDialog = ({ open, onOpenChange, employee, headers, onSaved }) => {
  const [salary, setSalary] = useState("");
  const [reasonType, setReasonType] = useState("aumento");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [effective, setEffective] = useState(new Date().toISOString().slice(0, 10));
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !employee) return;
    setSalary(employee.base_salary ? fromBaseCurrency(employee.base_salary).toString() : "");
    setReasonType("aumento"); setReason(""); setNotes(""); setEffective(new Date().toISOString().slice(0, 10));
    axios.get(`${API_URL}/hr/employees/${employee.user_id}/salary-history`, { headers, withCredentials: true })
      .then((r) => setHistory(r.data)).catch(() => setHistory([]));
  }, [open, employee]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = employee?.base_salary || 0;
  const next = toBaseCurrency(salary);
  const diff = next - current;
  const pct = current > 0 ? (diff / current) * 100 : null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/hr/employees/${employee.user_id}/salary`,
        { base_salary: next, reason_type: reasonType, reason, notes, effective_date: effective }, { headers, withCredentials: true });
      toast.success("Salário ajustado e registado na auditoria");
      onOpenChange(false); onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao ajustar salário"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="salary-adjust-dialog">
        <DialogHeader><DialogTitle>Ajuste de Salário — {employee?.full_name || employee?.name}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3 rounded-lg border bg-muted/40 p-3 text-sm">
            <div><p className="text-xs text-muted-foreground">Salário atual</p><p className="font-mono font-semibold" data-testid="salary-current">{formatCurrency(current)}</p></div>
            <div><p className="text-xs text-muted-foreground">Variação</p>
              <p className={`font-mono font-semibold ${diff > 0 ? "text-emerald-600" : diff < 0 ? "text-red-600" : ""}`} data-testid="salary-diff">
                {salary === "" ? "—" : `${diff >= 0 ? "+" : "−"}${formatCurrency(Math.abs(diff))}${pct !== null ? ` (${diff >= 0 ? "+" : ""}${pct.toLocaleString("pt-PT", { maximumFractionDigits: 1, minimumFractionDigits: 1 })}%)` : ""}`}
              </p></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Novo salário base</Label>
              <Input type="number" step="0.01" min="0" required value={salary} onChange={(e) => setSalary(e.target.value)} data-testid="salary-input" /></div>
            <div className="space-y-1"><Label>Data de efeito</Label>
              <Input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} data-testid="salary-effective-input" /></div>
          </div>
          <div className="space-y-1"><Label>Tipo de ajuste</Label>
            <Select value={reasonType} onValueChange={setReasonType}>
              <SelectTrigger data-testid="salary-reason-type"><SelectValue /></SelectTrigger>
              <SelectContent>{REASON_TYPES.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`reason-type-${k}`}>{l}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1"><Label>Justificativa <span className="text-destructive">*</span></Label>
            <Textarea required minLength={5} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explique o motivo do ajuste (fica registado permanentemente)" data-testid="salary-reason-input" /></div>
          <div className="space-y-1"><Label>Notas internas (opcional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex.: aprovado pela direção em reunião de 05/06" data-testid="salary-notes-input" /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving || salary === ""} data-testid="save-salary-btn">{saving ? "A guardar..." : "Confirmar ajuste"}</Button>
          </DialogFooter>
        </form>
        <SalaryHistoryList history={history} />
      </DialogContent>
    </Dialog>
  );
};
